import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import RadioButton from 'components/Common/RadioGroup';
import ChangePhoto from 'pages/Account/ChangePhoto';
import BasketPickerAndDateItem from 'components/Home/BasketPickerAndDateItem';
import { addNewBasketData, removeBasketImage, updateBasketDetail, updateBasketImage, transitionBasketStatus } from 'services/basketsApi';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import routes from 'constants/routes';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { convertToISOString } from 'utils/helpers';
import { notify } from 'components/Common/Toast';
import ErrorMessage from 'components/Common/ErrorMessage';
import { fetchBasketDetail } from 'redux/slices/baskets/basketDetailsSlice';
import { isSystemBasket } from 'types/baskets';
import { getProblemDetail } from 'api/v1Helpers';
import MuiLoader from 'components/Common/Loader';
import CardWrapper from 'components/Common/CardWrapper';
import { Basket } from 'types/common';
import { goalAmount } from 'appConfig';
import { selectSelectedParishId } from 'redux/selectors/parishContext';

type FormKeys = keyof typeof defaultValues;

/** A suspend schedule that can only be sent once the basket is confirmed ACTIVE — see suspendMode
 *  ('deferred') and the sequencing in createBasket/updateBasket. */
type DeferredSuspend = { nextPlannedStatus: 'SUSPENDED'; nextPlannedStatusAt: string } | null;

const defaultValues = {
    name: '',
    description: '',
    subType: 'GIVING',
    startDate: '',
    // Bound to whichever suspend picker is showing — a DRAFT's optional follow-up suspension
    // (paired with a scheduled activation) or an ACTIVE basket's independent scheduled suspension.
    // Which backend field it becomes at submit time depends on basket status; see onFormSubmit.
    suspendAt: '',
    // timeZone: "America/Chicago",
    // estimatedContributors: 0,
    // currencyCode: "840",
    type: 'OPEN',
    goalAmount,
};
const ACTIVE_IMMEDIATELY = '@catholic/ACTIVE_IMMEDIATELY';
const ACTIVE_SCHEDULED = '@catholic/ACTIVE_SCHEDULED';
const SUSPEND_SCHEDULED = '@catholic/SUSPEND_SCHEDULED';
const SUSPEND_MANUAL = '@catholic/SUSPEND_MANUAL';
const END_DATE_ERROR = '@catholic/END_DATE_ERROR';
const SUSPEND_AT_ERROR = '@catholic/SUSPEND_AT_ERROR';
const START_DATE_ERROR = '@catholic/START_DATE_ERROR';
const NAME_ERROR = '@catholic/NAME_ERROR';

interface DateErrorTypes {
    endDate: boolean;
    suspendAt: boolean;
    startDate: boolean;
    title: boolean;
}

interface CreateBasketSectionProps {
    title: string;
    children: React.ReactNode;
}

const initialDateErrorState: DateErrorTypes = {
    endDate: false,
    suspendAt: false,
    startDate: false,
    title: false,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
const dateErrorReducer = (state: DateErrorTypes, action: any) => {
    switch (action.type) {
        case END_DATE_ERROR:
            return {
                ...state,
                endDate: action.payload,
            };
        case SUSPEND_AT_ERROR:
            return {
                ...state,
                suspendAt: action.payload,
            };
        case START_DATE_ERROR:
            return {
                ...state,
                startDate: action.payload,
            };
        case NAME_ERROR:
            return {
                ...state,
                title: action.payload,
            };
        default:
            return state;
    }
};

const Section = ({ title, children }: CreateBasketSectionProps) => {
    return (
        <Stack width={'100%'} mt={4}>
            <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400, ml: { xs: 2, md: 8 } }} variant="h1">
                {title}
            </Typography>
            <Divider />
            <Stack gap={1} mx={{ xs: 4, md: 16 }} my={2}>
                {children}
            </Stack>
        </Stack>
    );
};

function CreateBasket() {
    //TODO: NEED TO FIX THE IS ARCHDIOCESE VARIABLE BASED ON THE ROLE
    const isArchdiocese = false;
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState<boolean>(false);
    const [imgValue, setImgValue] = useState('');
    const [imgLoading, setImgLoading] = useState(false);
    const [activationType, setActivationType] = useState<string>(ACTIVE_IMMEDIATELY);
    const [suspendType, setSuspendType] = useState<string>(SUSPEND_MANUAL);
    // Explicit user intent, tracked separately from the radio's visual/default state — set ONLY
    // by the user interacting with that section's controls, never by hydration. A DRAFT with no
    // schedule must not activate just because the Activation radio's untouched default happens to
    // read "Activate Immediately"; only an explicit touch may trigger that call or resend that
    // section's schedule fields on edit. On Create there is no prior state to protect, so an
    // untouched "Activate Immediately" default is fine there (see onFormSubmit).
    const [activationTouched, setActivationTouched] = useState(false);
    const [suspendTouched, setSuspendTouched] = useState(false);
    const { basketDetails, loading: basketDetailLoading } = useSelector((state: RootState) => state?.baskets?.details);
    const { userInfo } = useSelector((state: RootState) => state.user);
    const selectedParishId = useSelector(selectSelectedParishId);
    const [dateError, dateErrorDispatch] = useReducer(dateErrorReducer, initialDateErrorState);
    const { firstName, lastName } = userInfo || {};
    const { id } = useParams();
    const { handleSubmit, setValue, getValues, watch } = useForm({
        defaultValues: { ...defaultValues, firstName, lastName },
    });
    const disableActivationDate = activationType === ACTIVE_IMMEDIATELY;
    const disableSuspendDate = suspendType === SUSPEND_MANUAL;
    const basketStatus = basketDetails?.status;
    // Editing the parish's General Fund: it is always ACTIVE and the backend refuses any
    // scheduled status transition on it, so no scheduling section is shown. Name, description,
    // image and goal stay editable. Guarded on the loaded detail matching the route id so a stale
    // basket in the store cannot hide the section for a different basket.
    const isSystemFund = !!id && String(id) === String(basketDetails?.id) && isSystemBasket(basketDetails);

    // A DRAFT (create, or editing a still-DRAFT basket) may schedule an activation, optionally
    // with a follow-up suspension. An ACTIVE basket may independently schedule a suspension, and
    // never activation again. SUSPENDED/CLOSED baskets never reach this form at all (no Edit
    // entry point — see BasketDetail.tsx).
    const isDraftBasket = !id || basketStatus === 'PENDING';
    const isActiveBasket = !!id && basketStatus === 'ACTIVE';
    const showActivationSection = isDraftBasket && !isSystemFund;
    // The backend has exactly one pending-transition slot: a suspension can only ever be
    // scheduled (nextPlannedStatus='SUSPENDED') once the basket is ACTUALLY ACTIVE — it is not
    // representable in the same request as a DRAFT's create/activation. That gives three distinct
    // suspend shapes, mutually exclusive by basket state:
    //  - 'followUp': DRAFT + Scheduled Activation — sent as followUpSuspensionAt, paired with
    //    nextPlannedStatus=ACTIVE in the SAME create/PATCH request (one atomic call).
    //  - 'deferred': DRAFT + Activate Immediately — the basket isn't ACTIVE yet when this form is
    //    submitted, so the suspend can't be included in that same request. It is instead sent as
    //    its own nextPlannedStatus='SUSPENDED' PATCH immediately AFTER the activation call
    //    succeeds (see createBasket/updateBasket) — the only order the backend's own reachability
    //    rule (SUSPENDED only from ACTIVE) allows, not a workaround.
    //  - 'independent': editing an already-ACTIVE basket — ordinary nextPlannedStatus='SUSPENDED'
    //    PATCH, no activation involved at all.
    const suspendMode: 'followUp' | 'deferred' | 'independent' | 'none' = showActivationSection
        ? activationType === ACTIVE_SCHEDULED
            ? 'followUp'
            : 'deferred'
        : isActiveBasket && !isSystemFund
          ? 'independent'
          : 'none';
    const showSuspendSection = suspendMode !== 'none';

    const setFormValuesFromBasket = useCallback(
        (basket: Basket) => {
            (Object.keys(defaultValues) as FormKeys[]).forEach((key) => {
                const value = basket[key as keyof Basket];
                if (typeof value === 'string' || typeof value === 'number') {
                    setValue(key, value);
                } else {
                    // Handle other types if necessary, e.g., nested objects or null values
                    setValue(key, defaultValues[key]);
                }
            });
        },
        [setValue]
    );

    useEffect(() => {
        if (id && String(id) === String(basketDetails?.id) && basketDetails) {
            const { imgUrl, nextPlannedStatus, nextPlannedStatusAt, followUpSuspensionAt } = basketDetails;
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- pre-existing short-circuit-as-conditional idiom
            imgUrl && setImgValue(imgUrl);
            setFormValuesFromBasket(basketDetails);
            // Read the real schedule fields directly — never infer from startDate, and never
            // mark these hydration-driven writes as "touched" (that flag exists precisely to
            // distinguish this from real user intent).
            if (nextPlannedStatus === 'ACTIVE') {
                setActivationType(ACTIVE_SCHEDULED);
                if (nextPlannedStatusAt) setValue('startDate', nextPlannedStatusAt);
                if (followUpSuspensionAt) {
                    setSuspendType(SUSPEND_SCHEDULED);
                    setValue('suspendAt', followUpSuspensionAt);
                }
            } else if (nextPlannedStatus === 'SUSPENDED' && nextPlannedStatusAt) {
                setSuspendType(SUSPEND_SCHEDULED);
                setValue('suspendAt', nextPlannedStatusAt);
            }
        } else if (id && String(id) !== String(basketDetails?.id)) {
            dispatch(fetchBasketDetail(id));
        }
    }, [id, basketDetails, dispatch, setValue, setFormValuesFromBasket]);

    const { suspendAt, startDate } = getValues();
    const name = watch('name');

    const checkDateAndTimeError = (date: string | Date): boolean => {
        if (!date) return true;
        return dayjs(convertToISOString(date)).isBefore(dayjs(convertToISOString('')));
    };

    const handleError = (type: string, value: string | Date) => {
        let error = false;
        if (type === NAME_ERROR) {
            error = !value;
        } else {
            error = checkDateAndTimeError(value);
        }
        dateErrorDispatch({ type, payload: error });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const updateBasket = async (payload: any, activateNow: boolean, deferredSuspend: DeferredSuspend) => {
        if (!selectedParishId || !id) {
            notify('No selected parish', 'error');
            return;
        }
        // CHECKING FOR STRING BECAUSE IF IT'S STRING THEN IMAGE IS NOT UPDATED BY THE USER
        if (imgValue && typeof imgValue !== 'string') {
            try {
                setImgLoading(true);
                await updateBasketImage(selectedParishId, id, imgValue as unknown as Blob);
                dispatch(fetchBasketDetail(id));
                setImgLoading(false);
            } catch (e) {
                setImgLoading(false);
                notify(getProblemDetail(e) || 'Failed to update basket image', 'error');
            }
        } else if (basketDetails?.imgUrl && !imgValue) {
            try {
                setImgLoading(true);
                await removeBasketImage(selectedParishId, id);
                dispatch(fetchBasketDetail(id));
                setImgLoading(false);
            } catch (e) {
                setImgLoading(false);
                notify(getProblemDetail(e) || 'Failed to remove basket image', 'error');
            }
        }
        try {
            setLoading(true);
            await updateBasketDetail(selectedParishId, id, payload);
            if (activateNow) {
                await transitionBasketStatus(selectedParishId, id, 'ACTIVE');
            }
            if (deferredSuspend) {
                // Only reachable once the basket is ACTUALLY ACTIVE (activateNow above, or it
                // already was) — nextPlannedStatus='SUSPENDED' 409s from any other status.
                await updateBasketDetail(selectedParishId, id, deferredSuspend);
            }
            notify('Basket updated successfully!', 'success');
            navigate(routes.home.basketDetails.replace(':id', String(id)), { replace: true });
        } catch (error) {
            // A concurrent status/schedule change while this form was open comes back as a 409.
            // Blindly retrying the same (now-stale) PATCH would either fail again or clobber
            // whatever changed server-side, so instead: show the real backend detail, then
            // reload the basket so the form rebuilds itself from the latest server state on the
            // next render (the hydration effect above re-reads nextPlannedStatus/At/followUp).
            notify(getProblemDetail(error) || 'Failed to update basket', 'error');
            dispatch(fetchBasketDetail(id));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    const createBasket = async (payload: any, deferredSuspend: DeferredSuspend) => {
        if (!selectedParishId) {
            notify('No selected parish', 'error');
            return;
        }
        setLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        let created: any;
        try {
            created = await addNewBasketData(selectedParishId, payload);
        } catch (error) {
            notify(getProblemDetail(error) || 'Failed to create basket', 'error');
            setLoading(false);
            return;
        }
        // The basket now exists as DRAFT regardless of what happens next — never call
        // addNewBasketData again for this attempt. A failed immediate-activation below must not
        // read as "basket creation failed" (it didn't), and retrying it must operate on this same
        // basket, never create a second one.
        if (activationType === ACTIVE_IMMEDIATELY) {
            try {
                created = await transitionBasketStatus(selectedParishId, String(created.id), 'ACTIVE');
            } catch (error) {
                notify(getProblemDetail(error) || 'Basket was created as Draft, but activation failed', 'error');
                setLoading(false);
                // Land on the existing basket's Edit screen so a retry (explicitly re-selecting
                // Activate Immediately there) targets it, instead of creating another basket.
                navigate(routes.home.basketUpdate.replace(':id', String(created.id)), { replace: true });
                return;
            }
            if (deferredSuspend) {
                // The backend has no way to accept "activate now AND schedule a suspension" in
                // the create request — scheduling a suspension requires the basket to already be
                // ACTIVE, which it only just became above. This is the earliest point it can be
                // sent, not an avoidable create-then-PATCH split (unlike the scheduled-activation
                // + follow-up case, which the backend genuinely does accept atomically).
                try {
                    created = await updateBasketDetail(selectedParishId, String(created.id), deferredSuspend);
                } catch (error) {
                    notify(getProblemDetail(error) || 'Basket was activated, but scheduling the suspension failed', 'error');
                    setLoading(false);
                    navigate(routes.home.basketDetails.replace(':id', String(created.id)), { replace: true });
                    return;
                }
            }
        }
        notify('Basket created successfully!', 'success');
        navigate(routes.home.basketDetails.replace(':id', String(created.id)), { replace: true });
        setLoading(false);
    };

    const onFormSubmit = async () => {
        if (!name) {
            return handleError(NAME_ERROR, name);
        }
        if (showActivationSection && activationType === ACTIVE_SCHEDULED && !startDate) {
            return handleError(START_DATE_ERROR, startDate);
        }
        if (showSuspendSection && suspendType === SUSPEND_SCHEDULED && !suspendAt) {
            return handleError(SUSPEND_AT_ERROR, suspendAt);
        }
        // S > A: a follow-up suspension must be strictly after the scheduled activation. Only
        // applies to 'followUp' mode — 'deferred' and 'independent' suspensions aren't paired
        // with an activation timestamp to compare against (checked against CURRENT form values
        // regardless of which control was touched, so moving activation past an already-set,
        // untouched follow-up is still caught client-side).
        if (suspendMode === 'followUp' && suspendType === SUSPEND_SCHEDULED && startDate && suspendAt) {
            if (!dayjs(convertToISOString(suspendAt)).isAfter(dayjs(convertToISOString(startDate)))) {
                return handleError(SUSPEND_AT_ERROR, suspendAt);
            }
        }
        // A 'deferred' suspend only ever applies once the basket is confirmed ACTIVE. On Edit,
        // that requires the user to have ALSO explicitly touched Activation into "Activate
        // Immediately" (an untouched default must never activate — see the DRAFT/no-schedule
        // guard above). Without that, silently dropping the suspend selection would be exactly
        // the kind of swallowed user intent this whole feature exists to prevent — so it is
        // reported instead of ignored.
        if (id && suspendMode === 'deferred' && suspendTouched && suspendType === SUSPEND_SCHEDULED && !activationTouched) {
            notify('Select "Activate Immediately" to schedule a suspension on this still-Draft basket.', 'warning');
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
        const formValues: any = getValues();
        let activateNow = false;

        if (showActivationSection) {
            if (activationType === ACTIVE_SCHEDULED) {
                if (!id || activationTouched) {
                    // startDate is kept as the picked date too (informational "Started" display,
                    // matches existing product behavior) — nextPlannedStatusAt is the real
                    // schedule the backend acts on.
                    formValues.nextPlannedStatus = 'ACTIVE';
                    formValues.nextPlannedStatusAt = formValues.startDate;
                } else {
                    // Hydrated schedule, never touched during this edit — leave it exactly as it
                    // was; formValues.startDate already reflects the same untouched value.
                    delete formValues.nextPlannedStatus;
                }
            } else if (!id) {
                // Create, Activate Immediately — the ordinary default flow (case 1).
                formValues.startDate = convertToISOString(new Date());
            } else if (activationTouched) {
                // Edit, EXPLICITLY switched to Activate Immediately — cancel any pending schedule
                // and fire the real transition now.
                formValues.nextPlannedStatus = null;
                formValues.startDate = convertToISOString(new Date());
                activateNow = true;
            } else {
                // Edit, untouched default — a content-only save must never activate the basket or
                // re-stamp startDate to "now" (the DRAFT/no-schedule accidental-activation guard).
                delete formValues.startDate;
            }
        } else {
            // Activation isn't editable here (ACTIVE basket) — never resend startDate or any
            // activation schedule field.
            delete formValues.startDate;
        }

        let deferredSuspend: DeferredSuspend = null;

        if (suspendMode === 'followUp') {
            // The create schema has no `null` variant for this field — Create only ever sends a
            // real value (case 3) or omits it entirely (cases 1/2/4), never `null`.
            if (suspendType === SUSPEND_SCHEDULED && (!id || suspendTouched)) {
                formValues.followUpSuspensionAt = formValues.suspendAt;
            } else if (id && suspendTouched) {
                formValues.followUpSuspensionAt = null;
            }
            // else: create+no-follow-up, or edit+untouched — omit, nothing to send/change.
        } else if (suspendMode === 'deferred') {
            // The basket isn't ACTIVE yet at the moment this request is built (create: about to
            // be, if Immediate; edit: only if activateNow fires below) — nextPlannedStatus can't
            // go in THIS payload. createBasket/updateBasket send it as a separate call right after
            // the activation succeeds. No suspendTouched check: on Edit, activateNow already means
            // the user explicitly chose Immediate, and that PATCH (nextPlannedStatus:null) clears
            // any hydrated follow-up server-side — so a Scheduled Suspend still on screen must be
            // re-sent here, touched or not, or it is silently lost.
            const willActivateNow = !id || activateNow;
            if (willActivateNow && suspendType === SUSPEND_SCHEDULED) {
                deferredSuspend = { nextPlannedStatus: 'SUSPENDED', nextPlannedStatusAt: formValues.suspendAt };
            }
        } else if (suspendMode === 'independent' && suspendTouched) {
            if (suspendType === SUSPEND_SCHEDULED) {
                formValues.nextPlannedStatus = 'SUSPENDED';
                formValues.nextPlannedStatusAt = formValues.suspendAt;
            } else {
                formValues.nextPlannedStatus = null;
            }
        }
        delete formValues.suspendAt; // UI-only field name; never sent as-is.

        if (id) {
            updateBasket(formValues, activateNow, deferredSuspend);
        } else {
            createBasket(formValues, deferredSuspend);
        }
    };
    const handleActivationTypeChange = (value: string) => {
        setActivationTouched(true);
        setActivationType(value);
    };
    const handleActivationDate = (date: string) => {
        setActivationTouched(true);
        setValue('startDate', date);
        if (activationType === ACTIVE_IMMEDIATELY) return;
        handleError(START_DATE_ERROR, date);
    };
    const handleSuspendTypeChange = (value: string) => {
        setSuspendTouched(true);
        setSuspendType(value);
    };
    const handleSuspendDate = (date: string) => {
        setSuspendTouched(true);
        setValue('suspendAt', date);
        if (suspendType === SUSPEND_MANUAL) return;
        handleError(SUSPEND_AT_ERROR, date);
    };
    const handleTitleChange = (key: FormKeys, value: string) => {
        setValue(key, value);
        handleError(NAME_ERROR, value);
    };

    return (
        <CardWrapper cardProps={{ sx: { p: 0 } }}>
            <MuiLoader absolute loading={basketDetailLoading} />
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <Section title="Basket Details">
                    <TextInput
                        name="name"
                        value={name}
                        setValue={handleTitleChange}
                        label="Title"
                        icon={null}
                        sx={{ mt: 2 }}
                        inputProps={{ maxLength: 255 }}
                        width="100%"
                        placeholder="Type Basket title"
                    />
                    <div>{dateError?.title ? <ErrorMessage message="Title is required" /> : null}</div>
                    {/* <TextInput type="number" label="Contact" icon={null} sx={{ mt: 2 }} width="100%" placeholder="type number only for testing" /> */}
                    <TextInput
                        name="description"
                        value={watch('description')}
                        setValue={setValue}
                        label="Basket Text"
                        icon={null}
                        sx={{ mt: 2, mb: 18, p: 0 }}
                        multiline
                        minRows={4}
                        maxRows={4}
                        inputProps={{ maxLength: 255 }}
                        width="100%"
                        placeholder="Type text here..."
                    />
                </Section>
                <Section title="Basket Type">
                    <div style={{ marginBottom: 18, marginTop: 18 }}>
                        <RadioButton
                            alignItems="flex-start"
                            data={[
                                { value: 'GIVING', label: 'Giving Basket (Tax-deductible contributions)' },
                                { value: 'PAYMENT', label: 'Payment Basket (Payment for goods or services)', defaultValue: 'GIVING' },
                            ]}
                            value={watch('subType')}
                            name="subType"
                            setValue={(value: string) => setValue('subType', value)}
                            disabled={!!id}
                            sx={{ gap: { xs: 2, sm: 0 } }}
                        />
                    </div>
                </Section>
                {id && (
                    <ChangePhoto
                        isLoading={imgLoading}
                        imgValue={imgValue}
                        setImgValue={setImgValue}
                        tabProps={{ sx: { mx: { md: 8 } } }}
                        stackProps={{ mx: { md: 14 } }}
                    />
                )}
                <br />

                {showActivationSection && (
                    <Section title="Activation Details">
                        <BasketPickerAndDateItem
                            data={[
                                { value: ACTIVE_IMMEDIATELY, label: 'Activate Immediately' },
                                { value: ACTIVE_SCHEDULED, label: 'Scheduled Activation' },
                            ]}
                            setDate={handleActivationDate}
                            date={watch('startDate')}
                            type={activationType}
                            setType={handleActivationTypeChange}
                            name="activation"
                            disableRadio={!!id && basketStatus !== 'PENDING'}
                            disableDate={disableActivationDate}
                            disableTime={disableActivationDate}
                            defaultNull={disableActivationDate}
                        >
                            <div>{dateError.startDate ? <ErrorMessage /> : null}</div>
                        </BasketPickerAndDateItem>
                    </Section>
                )}
                {showSuspendSection && (
                    <Section title={suspendMode === 'followUp' ? 'Follow-up Suspension' : 'Suspend Details'}>
                        <BasketPickerAndDateItem
                            data={[
                                { value: SUSPEND_MANUAL, label: 'Manual Suspend' },
                                { value: SUSPEND_SCHEDULED, label: 'Scheduled Suspend' },
                            ]}
                            date={watch('suspendAt')}
                            setDate={handleSuspendDate}
                            setType={handleSuspendTypeChange}
                            type={suspendType}
                            name="suspend"
                            disableDate={disableSuspendDate}
                            disableTime={disableSuspendDate}
                            defaultNull={disableSuspendDate}
                        >
                            <div>{dateError.suspendAt ? <ErrorMessage /> : null}</div>
                        </BasketPickerAndDateItem>
                    </Section>
                )}
                {isArchdiocese && (
                    <Section title="Archdiocese Settings">
                        <Stack ml={-4}>
                            <Typography variant="body1">
                                Sherlock Holmes had opened his mouth to reply, when the door flew open, and Peterson, the commissionaire,
                                rushed into the apartment with flushed cheeks and the face of a man who is dazed with astonishment.
                            </Typography>
                        </Stack>
                        <div style={{ marginBottom: 18, marginTop: 18 }}>
                            <RadioButton
                                alignItems="flex-start"
                                data={[
                                    { value: 'specific', label: 'Archdiocese specific' },
                                    { value: 'wide', label: 'Archdiocese wide' },
                                ]}
                                value="specific"
                            />
                        </div>
                    </Section>
                )}

                <Stack maxWidth={{ xs: '250px', sm: '372px' }} m="auto" sx={{ mt: 2, mb: 4, px: 1 }}>
                    <Button width="100%" title="Save" loading={loading || imgLoading} disabled={!name?.trim()} type="submit" />
                </Stack>
            </form>
        </CardWrapper>
    );
}

export default CreateBasket;
